/**
 * The date filters.
 *
 * Thirty-nine of the ninety-one, and the only family whose answer depends on
 * when it is asked. All of them work the same way: the filter value names a
 * period -- today, this month, three weeks ago, an exact date -- which is turned
 * into a half-open interval `[lower, upper)` in a timezone, and the row's date
 * is placed relative to it. What varies between the filters is only that last
 * placement, which is why they are one function each here.
 *
 * The interval is half-open throughout, so "is today" and "is tomorrow" cannot
 * both match one instant at midnight.
 */
import { TZDate } from '@date-fns/tz'
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns'

import { getFieldTimezone, parseDateValue, type DateFieldLike } from '../fields/dates'
import type { Field } from '@/api/types'
import type { MatchResult } from './types'

/** Separates the timezone, value and operator inside a stored filter value. */
export const DATE_FILTER_VALUE_SEPARATOR = '?'

/**
 * Splits a multi-step filter value into its three parts.
 *
 * Stored as `timezone?value?operator`, with the operator last so that the older
 * two-part `timezone?value` form still reads correctly -- which is how the
 * deprecated single-step filters and the multi-step ones share one storage
 * format.
 */
export function splitMultiStepDateValue(
  value: string,
): [timezone: string | null, value: string, operator: string] {
  const parts = value.split(DATE_FILTER_VALUE_SEPARATOR)
  if (parts.length === 3) {
    return [parts[0] || null, parts[1] ?? '', parts[2] ?? '']
  }
  if (parts.length === 2) {
    // No timezone was stored; the two parts are the value and the operator.
    return [null, parts[0] ?? '', parts[1] ?? '']
  }
  return [null, '', '']
}

/** Splits the older two-part `timezone?value` form. */
export function splitTimezoneAndValue(
  value: string,
): [timezone: string | null, value: string] {
  const index = value.indexOf(DATE_FILTER_VALUE_SEPARATOR)
  if (index === -1) return [null, value]
  return [value.slice(0, index) || null, value.slice(index + 1)]
}

/* ------------------------------------------------------------- operators */

/** The period a filter value names. */
export type DateOperator =
  | 'today'
  | 'yesterday'
  | 'tomorrow'
  | 'one_week_ago'
  | 'this_week'
  | 'next_week'
  | 'one_month_ago'
  | 'this_month'
  | 'next_month'
  | 'one_year_ago'
  | 'this_year'
  | 'next_year'
  | 'nr_days_ago'
  | 'nr_days_from_now'
  | 'nr_weeks_ago'
  | 'nr_weeks_from_now'
  | 'nr_months_ago'
  | 'nr_months_from_now'
  | 'nr_years_ago'
  | 'nr_years_from_now'
  | 'exact_date'

/**
 * The start of the week, as the filters mean it.
 *
 * Upstream takes moment's Sunday-based start of week and adds a day. For a
 * Sunday that lands on the *following* Monday rather than the preceding one, so
 * "this week" on a Sunday excludes the Sunday itself. That is reproduced here
 * rather than corrected, because the stored filters and the ported specs both
 * assume it; `startOfWeek(date, { weekStartsOn: 1 })` is the version that would
 * fix it.
 */
function weekStart(date: Date): Date {
  return addDays(startOfWeek(date), 1)
}

interface OperatorSpec {
  /** Moves "now" to the instant the period is anchored on. */
  anchor?: (now: Date, value: string, timezone: string | null) => Date
  /** The half-open interval the anchor names. */
  bounds: (anchored: Date) => [Date, Date]
  /** Whether the operator reads a number from the filter value. */
  takesNumber?: boolean
}

/**
 * The offset an `nr_*` operator applies.
 *
 * A value that is not a number counts as zero, so "three-ish weeks ago" reads
 * as "this week" rather than matching nothing. That is moment's behaviour --
 * `subtract(NaN, 'days')` leaves the date untouched -- and the filters were
 * written against it.
 */
function number(value: string): number {
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

const DATE_OPERATORS = {
  today: { bounds: (d) => [startOfDay(d), addDays(startOfDay(d), 1)] },
  yesterday: {
    anchor: (now) => addDays(now, -1),
    bounds: (d) => [startOfDay(d), addDays(startOfDay(d), 1)],
  },
  tomorrow: {
    anchor: (now) => addDays(now, 1),
    bounds: (d) => [startOfDay(d), addDays(startOfDay(d), 1)],
  },
  one_week_ago: {
    anchor: (now) => addWeeks(now, -1),
    bounds: (d) => [weekStart(d), addWeeks(weekStart(d), 1)],
  },
  this_week: { bounds: (d) => [weekStart(d), addWeeks(weekStart(d), 1)] },
  next_week: {
    anchor: (now) => addWeeks(now, 1),
    bounds: (d) => [weekStart(d), addWeeks(weekStart(d), 1)],
  },
  one_month_ago: {
    anchor: (now) => addMonths(now, -1),
    bounds: (d) => [startOfMonth(d), addMonths(startOfMonth(d), 1)],
  },
  this_month: { bounds: (d) => [startOfMonth(d), addMonths(startOfMonth(d), 1)] },
  next_month: {
    anchor: (now) => addMonths(now, 1),
    bounds: (d) => [startOfMonth(d), addMonths(startOfMonth(d), 1)],
  },
  one_year_ago: {
    anchor: (now) => addYears(now, -1),
    bounds: (d) => [startOfYear(d), addYears(startOfYear(d), 1)],
  },
  this_year: { bounds: (d) => [startOfYear(d), addYears(startOfYear(d), 1)] },
  next_year: {
    anchor: (now) => addYears(now, 1),
    bounds: (d) => [startOfYear(d), addYears(startOfYear(d), 1)],
  },
  nr_days_ago: {
    anchor: (now, v) => addDays(now, -number(v)),
    bounds: (d) => [startOfDay(d), addDays(startOfDay(d), 1)],
    takesNumber: true,
  },
  nr_days_from_now: {
    anchor: (now, v) => addDays(now, number(v)),
    bounds: (d) => [startOfDay(d), addDays(startOfDay(d), 1)],
    takesNumber: true,
  },
  nr_weeks_ago: {
    anchor: (now, v) => addWeeks(now, -number(v)),
    bounds: (d) => [weekStart(d), addWeeks(weekStart(d), 1)],
    takesNumber: true,
  },
  nr_weeks_from_now: {
    anchor: (now, v) => addWeeks(now, number(v)),
    bounds: (d) => [weekStart(d), addWeeks(weekStart(d), 1)],
    takesNumber: true,
  },
  nr_months_ago: {
    anchor: (now, v) => addMonths(now, -number(v)),
    bounds: (d) => [startOfMonth(d), addMonths(startOfMonth(d), 1)],
    takesNumber: true,
  },
  nr_months_from_now: {
    anchor: (now, v) => addMonths(now, number(v)),
    bounds: (d) => [startOfMonth(d), addMonths(startOfMonth(d), 1)],
    takesNumber: true,
  },
  nr_years_ago: {
    anchor: (now, v) => addYears(now, -number(v)),
    bounds: (d) => [startOfYear(d), addYears(startOfYear(d), 1)],
    takesNumber: true,
  },
  nr_years_from_now: {
    anchor: (now, v) => addYears(now, number(v)),
    bounds: (d) => [startOfYear(d), addYears(startOfYear(d), 1)],
    takesNumber: true,
  },
  exact_date: {
    anchor: (_now, value, timezone) => {
      const parsed = exactDate(value, timezone)
      if (parsed === null) throw new InvalidFilterDate()
      return parsed
    },
    bounds: (d) => [startOfDay(d), addDays(startOfDay(d), 1)],
  },
} satisfies Record<DateOperator, OperatorSpec>

/** Raised when a filter value does not name a date, so the filter matches nothing. */
class InvalidFilterDate extends Error {}

/**
 * Reads an exact `YYYY-MM-DD` filter value as midnight in the filter's zone.
 *
 * Strict: anything that is not exactly ten characters of that shape is not a
 * date. Upstream parses in strict mode for the same reason -- a partially typed
 * "2021-0" must not be read as some nearby day.
 */
function exactDate(value: string, timezone: string | null): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match === null) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]

  // Built from the calendar parts rather than parsed from the string.
  // `new TZDate(string, zone)` reads the string as an instant and then *renders*
  // it in the zone, which for "2026-08-31" in New York gives the previous
  // evening. The filter means midnight local, which is what `TZDate.tz` builds.
  const at =
    timezone === null
      ? new Date(`${value}T00:00:00Z`)
      : TZDate.tz(timezone, year, month - 1, day, 0, 0, 0, 0)
  return Number.isNaN(at.getTime()) ? null : at
}

/** The current instant, read in the filter's timezone. */
function nowIn(timezone: string | null, now: Date): Date {
  return timezone === null ? new Date(now.getTime()) : new TZDate(now.getTime(), timezone)
}

/**
 * Reads a row's stored date in the filter's timezone.
 *
 * A value that is not a date becomes a date whose time is `NaN`, rather than a
 * null the caller has to special-case. Every comparison against it is false,
 * which is how an unreadable cell falls out of `is` and into `is not` -- the
 * same way an invalid moment behaves, and the reason `is not` shows the rows
 * whose date could not be read.
 */
function rowDateIn(value: unknown, timezone: string | null): Date {
  const parsed = parseDateValue(value)
  if (parsed === null) return new Date(NaN)
  return timezone === null ? parsed : new TZDate(parsed.getTime(), timezone)
}

/* --------------------------------------------------------------- matching */

/**
 * How a filter places a row's date against the interval.
 *
 * `now` is passed through because `date_is_within` needs it: that filter spans
 * from today to the named period, in whichever direction the period lies.
 */
export type DatePlacement = (
  rowDate: Date,
  lower: Date,
  upper: Date,
  now: Date,
) => boolean

const isEqual: DatePlacement = (row, lower, upper) =>
  row.getTime() >= lower.getTime() && row.getTime() < upper.getTime()

export const DATE_PLACEMENTS = {
  is: isEqual,
  isNot: (row, lower, upper, now) => !isEqual(row, lower, upper, now),
  isBefore: (row, lower) => row.getTime() < lower.getTime(),
  isOnOrBefore: (row, _lower, upper) => row.getTime() < upper.getTime(),
  isAfter: (row, _lower, upper) => row.getTime() >= upper.getTime(),
  isOnOrAfter: (row, lower) => row.getTime() >= lower.getTime(),
  /**
   * Between today and the named period, whichever way round they fall.
   *
   * "Within the next three weeks" and "within the past three weeks" are the
   * same filter with the interval on either side of today, so the span is taken
   * from whichever of the two comes first.
   */
  isWithin: (row, lower, upper, now) => {
    const startOfToday = startOfDay(now)
    let start = lower
    let end = upper
    if (startOfToday.getTime() < upper.getTime()) {
      start = startOfToday
    } else {
      end = addDays(startOfToday, 1)
    }
    return row.getTime() >= start.getTime() && row.getTime() < end.getTime()
  },
  /**
   * The span `has_not_date_within` negates, which is not `isWithin`.
   *
   * It runs from the start of today to the upper bound with no swap, so for a
   * period in the past the span is empty and the filter matches every row.
   * Upstream's positive and negative array "within" filters really do differ
   * this way; matching one to the other would change what stored views show.
   */
  isWithinFromToday: (row, _lower, upper, now) =>
    row.getTime() >= startOfDay(now).getTime() && row.getTime() < upper.getTime(),
} satisfies Record<string, DatePlacement>

/**
 * The interval a filter value names, or null when it names none.
 *
 * Returns `'all'` when the filter is incomplete -- an operator that reads a
 * number, with no number typed yet -- which shows every row rather than none.
 */
function intervalFor(
  filterValue: string,
  field: Field,
  now: Date,
  refuses: readonly DateOperator[] = [],
): { lower: Date; upper: Date; timezone: string | null } | 'all' | null {
  const [storedTimezone, value, operatorName] = splitMultiStepDateValue(filterValue)
  const spec = (DATE_OPERATORS as Record<string, OperatorSpec | undefined>)[operatorName]
  if (spec === undefined) return null
  if (refuses.includes(operatorName as DateOperator)) return null

  // A timezone is only meaningful for a field that carries a time.
  const timezone = storedTimezone ?? getFieldTimezone(field as DateFieldLike)

  if (spec.takesNumber === true && value === '') return 'all'

  let anchored: Date
  try {
    anchored = spec.anchor
      ? spec.anchor(nowIn(timezone, now), value, timezone)
      : nowIn(timezone, now)
  } catch {
    return null
  }
  if (Number.isNaN(anchored.getTime())) return null

  const [lower, upper] = spec.bounds(anchored)
  return { lower, upper, timezone }
}

/** Builds a date filter over a single date cell. */
export function dateFilter(place: DatePlacement, refuses: readonly DateOperator[] = []) {
  return (rowValue: unknown, filterValue: string, field: Field): MatchResult => {
    if (rowValue === null) return false
    const interval = intervalFor(filterValue, field, new Date(), refuses)
    if (interval === null) return false
    if (interval === 'all') return true

    const rowDate = rowDateIn(rowValue, interval.timezone)
    return place(
      rowDate,
      interval.lower,
      interval.upper,
      nowIn(interval.timezone, new Date()),
    )
  }
}

/**
 * Builds a date filter over a lookup of dates.
 *
 * `negate` rather than a negated placement, because "none of these dates is
 * today" is not "any of these dates is not today".
 */
export function arrayDateFilter(
  place: DatePlacement,
  negate = false,
  refuses: readonly DateOperator[] = [],
) {
  return (rowValue: unknown, filterValue: string, field: Field): MatchResult => {
    if (rowValue === null) return false
    const interval = intervalFor(filterValue, field, new Date(), refuses)
    if (interval === null) return false
    if (interval === 'all') return true
    if (!Array.isArray(rowValue)) return false

    const now = nowIn(interval.timezone, new Date())
    const matched = (rowValue as { value?: unknown }[]).some((item) =>
      place(
        rowDateIn(item.value, interval.timezone),
        interval.lower,
        interval.upper,
        now,
      ),
    )
    return negate ? !matched : matched
  }
}

/**
 * Builds one of the nineteen deprecated single-step date filters.
 *
 * Each is exactly a multi-step filter with its operator fixed, which is what
 * their own migration functions say -- so rather than reimplement nineteen
 * near-copies, they are expressed as the migration and then matched as the
 * filter they migrate to. Their stored value is the older two-part
 * `timezone?value` form, which is rewritten into the three-part one here.
 */
/**
 * How a deprecated filter treats a filter value it cannot use.
 *
 * The nineteen are not uniform, and the differences are load-bearing:
 * `date_before` shows every row when its date will not parse, `date_equal`
 * shows none, and `date_not_equal` shows all of them because it negates a
 * comparison that was already false.
 */
export interface DeprecatedDateOptions {
  /**
   * `abstain` shows every row, `reject` shows none, and `compare` goes ahead
   * with an unreadable date so that every comparison against it is false --
   * which a negating filter then turns into a match.
   */
  onUnusable?: 'abstain' | 'reject' | 'compare'
  /** Whether an empty value abstains whatever `onUnusable` says. */
  emptyAbstains?: boolean
  /** Whether a zero offset is refused: "within zero weeks" spans nothing. */
  rejectZero?: boolean
  /** What an empty cell answers, before the filter value is even read. */
  nullRow?: boolean
}

/**
 * Builds one of the nineteen deprecated single-step date filters.
 *
 * Each is a multi-step filter with its operator fixed -- which is what their own
 * migration functions say -- so they are expressed as that migration rather than
 * as nineteen near-copies. Their stored value is the older two-part
 * `timezone?value` form, rewritten into the three-part one here.
 */
export function deprecatedDateFilter(
  place: DatePlacement,
  operator: DateOperator,
  options: DeprecatedDateOptions = {},
) {
  const {
    onUnusable = 'abstain',
    emptyAbstains = true,
    rejectZero = false,
    nullRow = false,
  } = options
  const match = dateFilter(place)
  const needsDate = operator === 'exact_date'
  const needsNumber = operator.startsWith('nr_')

  return (rowValue: unknown, filterValue: string, field: Field): MatchResult => {
    // Only the abstaining filters look at their value before the cell. The rest
    // answer for an empty cell first, so a cleared `date_equal` still hides the
    // rows that have no date.
    if (onUnusable !== 'abstain' && rowValue === null) return nullRow

    const [timezone, value] = splitTimezoneAndValue(filterValue)
    const usable = needsDate
      ? /^\d{4}-\d{2}-\d{2}$/.test(value)
      : !needsNumber || !Number.isNaN(parseInt(value, 10))

    // `today` and the `this_*` operators read no value at all, so an empty one
    // is not an unfinished filter for them.
    const takesValue = needsDate || needsNumber
    if (takesValue && value === '' && emptyAbstains) return true
    if (!takesValue && rowValue === null) return nullRow
    if (rejectZero && needsNumber && parseInt(value, 10) === 0) return false

    // Order matters: the abstaining filters check the filter value before the
    // cell, so an unusable value shows even the rows with no date at all.
    if (!usable && onUnusable === 'abstain') return true
    if (rowValue === null) return nullRow
    if (!usable && onUnusable === 'reject') return false
    if (!usable) {
      // Compare against a date that will not read. Every comparison is false,
      // which a negating filter turns into a match -- upstream reaches the same
      // answer by comparing against an invalid moment.
      const zone = timezone ?? getFieldTimezone(field as DateFieldLike)
      const unreadable = new Date(NaN)
      return place(
        rowDateIn(rowValue, zone),
        unreadable,
        unreadable,
        nowIn(zone, new Date()),
      )
    }

    const sep = DATE_FILTER_VALUE_SEPARATOR
    return match(rowValue, `${timezone ?? ''}${sep}${value}${sep}${operator}`, field)
  }
}

/**
 * Whether the row falls on a given day of the month.
 *
 * The one date filter with no interval: it compares a single calendar number,
 * so a row on the 3rd of any month matches a filter of 3.
 */
export function matchesDateEqualsDayOfMonth(
  rowValue: unknown,
  filterValue: string,
  field: Field,
): MatchResult {
  const [storedTimezone, raw] = splitTimezoneAndValue(filterValue)
  if (raw === '') return true

  const dayOfMonth = parseInt(raw, 10)
  if (Number.isNaN(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) return false

  const timezone = storedTimezone ?? getFieldTimezone(field as DateFieldLike)
  const rowDate = rowDateIn(rowValue, timezone)
  if (rowDate === null) return false
  return rowDate.getDate() === dayOfMonth
}
